import { useState, useMemo } from 'react';
import { Search, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePropertyKitCategories, usePropertyKitItems } from '@/hooks/inventory';
import { usePropertyArticles } from '@/hooks/inventory/usePropertyArticles';
import { getThumbnailImageUrl } from '@/lib/cloudinary';

interface AddItemToPropertyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  existingItemIds: string[];
}

export default function AddItemToPropertyModal({
  open,
  onOpenChange,
  propertyId,
  existingItemIds,
}: AddItemToPropertyModalProps) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const { categories } = usePropertyKitCategories();
  const { items, isLoading } = usePropertyKitItems({
    categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
    search: search || undefined,
  });
  const { addEntry, isAdding } = usePropertyArticles(propertyId);

  // Filter out items already in the property
  const availableItems = useMemo(() => {
    return items.filter((item) => !existingItemIds.includes(item.id));
  }, [items, existingItemIds]);

  const selectedItem = availableItems.find((item) => item.id === selectedItemId);

  const handleAdd = async () => {
    if (!selectedItem) return;

    try {
      await addEntry({ itemId: selectedItem.id, item: selectedItem });
      setSelectedItemId(null);
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Agregar artículo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar artículo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Items list */}
          <div className="border rounded-lg max-h-[300px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : availableItems.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                {items.length === existingItemIds.length
                  ? 'Todos los artículos ya están en esta propiedad'
                  : 'No se encontraron artículos'}
              </div>
            ) : (
              <div className="divide-y">
                {availableItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedItemId(item.id)}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors ${
                      selectedItemId === item.id ? 'bg-primary/10' : ''
                    }`}
                  >
                    {item.image_url && (
                      <img
                        src={getThumbnailImageUrl(item.image_url)}
                        alt={item.name}
                        className="h-10 w-10 rounded object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="text-sm text-muted-foreground truncate">
                        {item.category?.name}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!selectedItemId || isAdding}
            className="bg-brand-primary hover:bg-brand-primary-hover"
          >
            {isAdding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Agregar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
